import type { MultiValue, SingleValue } from 'react-select';
import { useMemo, useState } from 'react';
import Select from 'react-select';
import { classNames } from '@library/utils';

export interface ComboboxOption<T = string | number> {
  label: string | number;
  value: T;
  isDisabled?: boolean;
}

export type ComboboxOptions<T = string | number> = Array<ComboboxOption<T>>;

interface ComboboxBaseProps {
  id: string;
  isMulti?: boolean;
  label?: string;
  labelClassName?: string;
  options?: ComboboxOptions;
  placeholder?: string;
  disabled?: boolean;
  hideSelectedOptions?: boolean;
  closeMenuOnSelect?: boolean;
  allowSelectAll?: boolean;
  isClearable?: boolean;
  valueClassName?: string;
  // className passthroughs for all of React Select's subcomponents
  clearIndicatorClassName?: string;
  containerClassName?: string;
  controlClassName?: string;
  dropdownIndicatorClassName?: string;
  groupClassName?: string;
  groupHeadingClassName?: string;
  indicatorsContainerClassName?: string;
  indicatorSeparatorClassName?: string;
  inputClassName?: string;
  loadingIndicatorClassName?: string;
  loadingMessageClassName?: string;
  menuClassName?: string;
  menuListClassName?: string;
  menuPortalClassName?: string;
  menuPortalTarget?: HTMLElement;
  multiValueClassName?: string;
  multiValueLabelClassName?: string;
  multiValueRemoveClassName?: string;
  noOptionsMessageClassName?: string;
  optionClassName?: string;
  placeholderClassName?: string;
  singleValueClassName?: string;
  valueContainerClassName?: string;
}

export interface SingleSelectComboboxProps extends ComboboxBaseProps {
  isMulti: false;
  value: ComboboxOption | null;
  onChange: (value: ComboboxOption | null) => void;
}

export interface MultiSelectComboboxProps extends ComboboxBaseProps {
  isMulti: true;
  value: ComboboxOptions | null;
  onChange: (value: ComboboxOptions | null) => void;
}

export function Combobox({
  id,
  isMulti = false,
  label = '',
  labelClassName = 'text-soClean-100 rounded-md text-xs uppercase leading-6',
  value,
  onChange,
  options = [],
  placeholder = 'Select a value...',
  disabled = false,
  hideSelectedOptions = true,
  closeMenuOnSelect = false,
  allowSelectAll = false,
  isClearable = true,
  valueClassName = '',
  // className passthroughs for all of React Select's subcomponents
  clearIndicatorClassName = 'bg-soFresh-110 text-soClean-140 p-2 hover:text-roses',
  containerClassName = 'bg-soFresh-110 border border-1 border-soFresh-140 rounded-md',
  controlClassName = 'bg-soFresh-110 rounded-md',
  dropdownIndicatorClassName = 'bg-soFresh-110 text-soClean-140 p-2 rounded-md',
  groupClassName = '',
  groupHeadingClassName = '',
  indicatorsContainerClassName = 'bg-soFresh-110 rounded-md',
  indicatorSeparatorClassName = 'bg-soClean-140 my-2',
  inputClassName = 'font-heebo text-[13px] mx-1 focus:shadow-none ring-transparent text-soClean-100',
  loadingIndicatorClassName = '',
  loadingMessageClassName = '',
  menuClassName = 'absolute top-full w-full z-1 bg-soFresh-110 rounded-md shadow-xs my-2 border border-1 border-soFresh-140',
  menuListClassName = 'max-h-72 overflow-y-auto relative py-1',
  menuPortalClassName = '',
  menuPortalTarget = document.body,
  multiValueClassName = 'flex m-0.5 rounded-xl bg-soFresh-130',
  multiValueLabelClassName = 'text-soClean-100 overflow-hidden text-ellipsis whitespace-nowrap rounded-sm pl-2.5 pr-0.25 py-0.5 text-[13px] font-heebo',
  multiValueRemoveClassName = 'text-soClean-140 flex align-center rounded-sm px-2 hover:text-roses',
  noOptionsMessageClassName = 'text-soClean-140 font-heebo text-[13px] mx-1',
  optionClassName = 'font-heebo text-[13px] px-3 py-1 rounded bg-soFresh-110',
  placeholderClassName = 'text-soClean-140 font-heebo text-[13px] mx-1',
  singleValueClassName = 'text-soClean-100 font-heebo text-[13px] mx-1',
  valueContainerClassName = 'flex flex-1 flex-wrap align-center relative px-2 py-0.5'
}: SingleSelectComboboxProps | MultiSelectComboboxProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const selectAllOption = useMemo(
    () => ({ value: 'SELECT_ALL', label: 'Select All' }),
    []
  );

  const fullOptions = useMemo(
    () =>
      allowSelectAll &&
      isMulti &&
      Array.isArray(value) &&
      value.length !== options.length
        ? [selectAllOption, ...options]
        : options,
    [allowSelectAll, isMulti, value, selectAllOption, options]
  );

  const handleOnChange = (
    newValue: SingleValue<ComboboxOption> | MultiValue<ComboboxOption>
  ) => {
    if (
      allowSelectAll &&
      isMulti &&
      Array.isArray(newValue) &&
      (newValue as ComboboxOptions).some((v) => v.value === 'SELECT_ALL')
    ) {
      onChange(options as ComboboxOption & ComboboxOptions);
      setMenuOpen(false);
    } else {
      onChange(newValue as ComboboxOption & ComboboxOptions);
    }
  };

  return (
    <>
      {label && (
        <label htmlFor={id} className={labelClassName}>
          {label}
        </label>
      )}
      <Select
        unstyled
        menuIsOpen={menuOpen}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
        value={value}
        onChange={handleOnChange}
        hideSelectedOptions={hideSelectedOptions}
        closeMenuOnSelect={closeMenuOnSelect}
        id={id}
        isClearable={isClearable}
        isDisabled={disabled}
        isOptionDisabled={(option) => option.isDisabled ?? false}
        isMulti={isMulti}
        menuPortalTarget={menuPortalTarget}
        menuPosition="fixed"
        options={fullOptions}
        placeholder={placeholder}
        classNames={{
          // Full list of keys for customizing React Select's inner components
          clearIndicator: () => clearIndicatorClassName,
          container: () => containerClassName,
          control: () => controlClassName,
          dropdownIndicator: () => dropdownIndicatorClassName,
          group: () => groupClassName,
          groupHeading: () => groupHeadingClassName,
          indicatorsContainer: () => indicatorsContainerClassName,
          indicatorSeparator: () => indicatorSeparatorClassName,
          input: () => inputClassName,
          loadingIndicator: () => loadingIndicatorClassName,
          loadingMessage: () => loadingMessageClassName,
          menu: () => menuClassName,
          menuList: () => menuListClassName,
          menuPortal: () => menuPortalClassName,
          multiValue: () => multiValueClassName,
          multiValueLabel: () => multiValueLabelClassName,
          multiValueRemove: () => multiValueRemoveClassName,
          noOptionsMessage: () => noOptionsMessageClassName,
          option: ({ isDisabled }) =>
            classNames(
              optionClassName,
              isDisabled
                ? 'text-soFresh-160'
                : 'text-soClean-100 hover:bg-stankonia-100'
            ),
          placeholder: () => placeholderClassName,
          singleValue: () => singleValueClassName,
          valueContainer: () => `${valueContainerClassName} ${valueClassName}`
        }}
        // necessary fix to combat box-shadow artifact from @tailwindcss/forms
        styles={{
          input: (base) => ({
            ...base,
            'input:focus': {
              boxShadow: 'none'
            }
          }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 })
        }}
      />
    </>
  );
}
